import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import SyncIcon from '@atlaskit/icon-lab/core/sync';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { DevDocsConnectionRow } from '@confluence/dev-docs/entry-points/DevDocsConnectionRow.ts';
import { DevDocsScmProviderIcon } from '@confluence/dev-docs/entry-points/DevDocsScmProviderIcon.ts';

import { ConnectionsByLineSectionShell } from '../connections-by-line-section/ConnectionsByLineSectionShell.tsx';

import type { SyncedFromData } from './useConnectionSectionsData.ts';
import type { SectionExpansionProps } from './useSectionExpansion.ts';

const styles = cssMap({
	// Clip the row's own hover background to the section card's radius.
	row: {
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',
	},
	// The provider logo is already a bordered tile, so it is shown bare rather
	// than through BylineConnectionIconStack, which would add a second border.
	headerIcon: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
	},
});

const i18n = defineMessages({
	syncedFrom: {
		id: 'byline-connections.preview-panel.section.synced-from',
		defaultMessage: 'Synced from',
		description:
			'Heading of the Teamwork Graph preview panel section naming the external source, such as a GitHub repository, that the current page is synced from. The section shows the number of sources in a badge beside the heading.',
	},
});

export type SyncedFromSectionProps = SyncedFromData & SectionExpansionProps;

/**
 * The panel's "Synced from" section. Renders nothing when the content is not in a Dev
 * Docs managed folder. `useConnectionSectionsData` supplies the connection.
 *
 * Uses `ConnectionsByLineSectionShell` rather than `ConnectionsByLineSection`
 * because a Dev Docs source is not a smart link, so it cannot be expressed as an
 * `EnhancedSmartLink` row.
 */
export const SyncedFromSection = ({
	connection,
	isExpanded,
	onToggleExpanded,
}: SyncedFromSectionProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!connection) {
		return null;
	}

	return (
		<ConnectionsByLineSectionShell
			title={formatMessage(i18n.syncedFrom)}
			count={1}
			iconBefore={<SyncIcon label="" spacing="none" />}
			trailing={
				<Box xcss={styles.headerIcon} aria-hidden="true">
					<DevDocsScmProviderIcon provider={connection.provider} />
				</Box>
			}
			isExpanded={isExpanded}
			onToggleExpanded={onToggleExpanded}
			testId="teamwork-graph-preview-panel-synced-from-section"
		>
			<Box xcss={styles.row}>
				<DevDocsConnectionRow connection={connection} />
			</Box>
		</ConnectionsByLineSectionShell>
	);
};
